import { useState, useEffect, useCallback } from 'react';
import { Package, Check, Clock, Loader2, AlertCircle, CheckCircle, Truck } from 'lucide-react';

const BASE = '/api/telegram/app';

function getInitData() {
  return (window as any).Telegram?.WebApp?.initData || '';
}

async function tgFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': getInitData(),
      ...(opts?.headers || {}),
    },
  });
  return res.json();
}

/** Wait for Telegram WebApp SDK to be available (loaded via index.html) */
function waitForTelegramSdk(timeout = 3000): Promise<boolean> {
  return new Promise((resolve) => {
    if ((window as any).Telegram?.WebApp?.initData) { resolve(true); return; }
    const start = Date.now();
    const check = () => {
      if ((window as any).Telegram?.WebApp?.initData) { resolve(true); return; }
      if (Date.now() - start > timeout) { resolve(false); return; }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

interface BatchItem {
  wh_item_id: number;
  ml_order_id: number;
  ml_shipment_id: number;
  asin: string;
  title: string;
  ship_status: string;
  ship_substatus: string;
  verified: boolean;
}

interface BatchData {
  batch_id: number;
  total: number;
  dropped_off: number;
  pending: number;
  items: BatchItem[];
}

export default function ReleaseMiniApp() {
  const batchId = window.location.pathname.match(/\/release\/(\d+)/)?.[1];
  const params = new URLSearchParams(window.location.search);
  const sellerId = params.get('seller_id') || '1';

  const [sdkReady, setSdkReady] = useState(false);
  const [data, setData] = useState<BatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(false);
  const [released, setReleased] = useState(false);
  const [releaseResult, setReleaseResult] = useState<any>(null);
  const [error, setError] = useState('');

  // Wait for Telegram SDK, then init
  useEffect(() => {
    waitForTelegramSdk().then((ready) => {
      if (ready) {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) { tg.ready(); tg.expand(); }
      }
      setSdkReady(true);
    });
  }, []);

  const load = useCallback(async () => {
    if (!batchId || !sdkReady) return;
    setLoading(true);
    try {
      const res = await tgFetch(`/release/${batchId}?seller_id=${sellerId}`);
      if (res.success) {
        setData(res.data);
      } else {
        setError(res.error || 'Error loading batch');
      }
    } catch {
      setError('Connection error');
    }
    setLoading(false);
  }, [batchId, sellerId, sdkReady]);

  useEffect(() => { load(); }, [load]);

  const handleRelease = async () => {
    if (!batchId) return;
    setReleasing(true);
    setError('');
    try {
      const res = await tgFetch(`/release/${batchId}?seller_id=${sellerId}`, { method: 'POST' });
      if (res.success) {
        setReleaseResult(res.data);
        if (res.data.released) {
          setReleased(true);
          (window as any).Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        } else {
          setError(`${res.data.pending} envios pendientes de confirmacion ML`);
          (window as any).Telegram?.WebApp?.HapticFeedback?.notificationOccurred('warning');
          load(); // Reload to show updated statuses
        }
      } else {
        setError(res.error || 'Release failed');
      }
    } catch {
      setError('Release failed');
    }
    setReleasing(false);
  };

  const isDark = ((window as any).Telegram?.WebApp?.colorScheme === 'dark') || window.matchMedia('(prefers-color-scheme: dark)').matches;
  const bg = isDark ? '#1e1e1e' : '#ffffff';
  const cardBg = isDark ? '#2d2d2d' : '#f8f9fa';
  const borderColor = isDark ? '#3d3d3d' : '#e5e7eb';
  const textPrimary = isDark ? '#ffffff' : '#111827';
  const textSecondary = isDark ? '#9ca3af' : '#6b7280';
  const green = '#22c55e';
  const yellow = '#eab308';

  if (released) {
    return (
      <div style={{ background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <CheckCircle size={64} color={green} />
        <p style={{ color: textPrimary, fontSize: 22, fontWeight: 700 }}>Batch Released</p>
        <p style={{ color: textSecondary, fontSize: 16 }}>Todos los envios confirmados por ML</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={40} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
        <AlertCircle size={48} color="#ef4444" />
        <p style={{ color: textPrimary, fontSize: 16 }}>{error || 'Batch not found'}</p>
      </div>
    );
  }

  return (
    <div style={{ background: bg, minHeight: '100vh', fontFamily: '-apple-system, system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Truck size={28} color="#3b82f6" />
          <div>
            <p style={{ color: textPrimary, fontSize: 20, fontWeight: 700 }}>Batch #{data.batch_id}</p>
            <p style={{ color: textSecondary, fontSize: 14 }}>{data.total} items · {data.dropped_off} confirmados</p>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div style={{ padding: '12px 20px' }}>
        <div style={{ height: 8, borderRadius: 4, background: isDark ? '#374151' : '#e5e7eb', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4,
            background: data.pending === 0 ? green : yellow,
            width: `${data.total > 0 ? (data.dropped_off / data.total) * 100 : 0}%`,
            transition: 'width 0.3s',
          }} />
        </div>
        <p style={{ color: textSecondary, fontSize: 12, marginTop: 4 }}>
          {data.dropped_off}/{data.total} dropped off
        </p>
      </div>

      {/* Items */}
      <div style={{ padding: '0 20px 20px' }}>
        {data.items.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', marginBottom: 6,
            borderRadius: 10, border: `1px solid ${borderColor}`,
            background: item.verified ? (isDark ? '#052e16' : '#f0fdf4') : cardBg,
          }}>
            {item.verified
              ? <Check size={22} color={green} />
              : <Clock size={22} color={textSecondary} />
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ color: textPrimary, fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.title || item.asin}
              </p>
              <p style={{ color: textSecondary, fontSize: 12 }}>
                #{item.ml_order_id} · {item.ship_status}/{item.ship_substatus || '—'}
              </p>
            </div>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
              background: item.verified ? (isDark ? '#166534' : '#dcfce7') : (isDark ? '#374151' : '#f3f4f6'),
              color: item.verified ? green : textSecondary,
            }}>
              {item.verified ? 'OK' : 'Pending'}
            </span>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ margin: '0 20px 12px', padding: '10px 14px', borderRadius: 10, background: isDark ? '#450a0a' : '#fef2f2', color: '#ef4444', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Release button */}
      <div style={{ padding: '0 20px 20px' }}>
        <button
          onClick={handleRelease}
          disabled={releasing}
          style={{
            width: '100%', padding: '16px', borderRadius: 14, border: 'none',
            background: data.pending === 0 ? green : '#3b82f6',
            color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer',
            opacity: releasing ? 0.6 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {releasing
            ? <><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Verificando...</>
            : <><Truck size={20} /> Confirmar Release</>
          }
        </button>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
